import { z } from "zod";
import { memberIdSchema, parameterValueSchema, standardParameterReferenceSchema } from "./common.schemas";
import { newCanonicalSchema } from "./entity-id.schemas";

const treatmentComponentSchema = z.object({
  id: memberIdSchema.optional(),
  label: z.string().trim().min(1),
  value: parameterValueSchema,
});

const treatmentObservationSchema = z.object({
  id: memberIdSchema.optional(),
  label: z.string().trim().min(1),
  repeatable: z.boolean(),
  noteLabel: z.string().trim().min(1).optional(),
  components: z.array(treatmentComponentSchema).min(1),
});

const treatmentParameterSchema = z.object({
  id: memberIdSchema.optional(),
  label: z.string().trim().min(1),
  value: parameterValueSchema,
});

const deviceRequirementSchema = z.object({ id: memberIdSchema.optional(), definition: z.string().url() });

const treatmentDefinitionContentSchema = z.object({
  procedureNoteLabel: z.string().trim().min(1).optional(),
  standardObservations: z.array(z.enum(["tolerance"])).default(["tolerance"]),
  observations: z.array(treatmentObservationSchema),
  parameters: z.array(z.union([treatmentParameterSchema, standardParameterReferenceSchema])).refine((entries) => {
    const keys = entries.flatMap((entry) => ("kind" in entry ? [entry.key] : []));
    return new Set(keys).size === keys.length;
  }, "Standard parameters must be unique"),
  requirements: z.object({
    devices: z.array(deviceRequirementSchema),
    location: z.string().trim().min(1).optional(),
  }),
});

export const defineTreatmentDefinitionInputSchema = treatmentDefinitionContentSchema.extend({
  definitionUrl: newCanonicalSchema,
  name: z.string().trim().min(1),
  description: z.string().trim().min(1).optional(),
});
export type DefineTreatmentDefinitionInput = z.infer<typeof defineTreatmentDefinitionInputSchema>;

export const reviseTreatmentDefinitionInputSchema = treatmentDefinitionContentSchema.extend({
  definitionUrl: z.string().url(),
  expectedVersion: z.number().int().positive(),
});
export type ReviseTreatmentDefinitionInput = z.infer<typeof reviseTreatmentDefinitionInputSchema>;

export const correctTreatmentDefinitionInputSchema = z.object({
  definitionUrl: z.string().url(),
  name: z.string().trim().min(1).optional(),
  description: z.string().trim().min(1).nullable().optional(),
});
export type CorrectTreatmentDefinitionInput = z.infer<typeof correctTreatmentDefinitionInputSchema>;

export const changeTreatmentDefinitionAvailabilityInputSchema = z.object({ definitionUrl: z.string().url() });
export type ChangeTreatmentDefinitionAvailabilityInput = z.infer<
  typeof changeTreatmentDefinitionAvailabilityInputSchema
>;
